/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { Command } from '../../../model/Command';
import { Filter } from '../../../model/Filter';
import { PreventiveSheet } from '../../../model/PreventiveSheet';
import { BaseController } from '../../BaseController';
import { PreventiveSheetAttachmentViewController } from './PreventiveSheetAttachmentView';

class PreventiveSheetViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, preventiveSheetService, siteService, equipmentService, dateTimeService,
        waitSpinnerService, $q, $state, $stateParams, $log, notificationService, $scope, filterService, dialogService,
                authenticationService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.preventiveSheetService = preventiveSheetService;
        this.siteService = siteService;
        this.equipmentService = equipmentService;
        this.dateTimeService = dateTimeService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.authenticationService = authenticationService;
        this.$log.debug('Controller \'PreventiveSheetView\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.commands = [];
        this.sites = [];
        this.equipments = [];
        this.checkConstraintViolation = null;

        // The updateMode flag can be used to know the context in which this controller is running: creation or update.
        let initPromise;
        let sitePromise;
        let equipmentPromise;
        if (this.$stateParams.hasOwnProperty('id')) {
            this.updateMode = true;
            this.title = 'model.preventivesheet.preventivesheet';
            initPromise = this.preventiveSheetService.getEntity(this.$stateParams.id);
            sitePromise = [];
            equipmentPromise = [];
        } else {
            this.updateMode = false;
            this.title = 'ui.preventivesheet.creation';
            initPromise = new PreventiveSheet();
            this.initMaxActionDate(initPromise);
            sitePromise = this.siteService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            });
            if (this.$stateParams.equipmentId) {
                equipmentPromise = sitePromise.then(site => this.getEquipments(site.id));
            } else {
                equipmentPromise = [];
            }
        }

        this.handleRestServiceCall(this.$q.all({
            entity: initPromise,
            sites: sitePromise,
            equipments: equipmentPromise
        }), data => {
            if (data.entity && angular.isString(data.entity.maxActionDate)) {
                data.entity.plannedWeek = this.dateTimeService.toPreventiveSheetPlannedWeek(data.entity.maxActionDate);
            }
            this.pristineEntity = new PreventiveSheet(data.entity);
            this.sites = data.sites;
            this.equipments = data.equipments;
            if (this.$stateParams.equipmentId) {
                this.pristineEntity.equipmentId = this.$stateParams.equipmentId;
                const selectedEquipment = this.equipments.find(
                    equipment => equipment.id === this.$stateParams.equipmentId
                );
                if (selectedEquipment) {
                    this.pristineEntity.siteId = selectedEquipment.siteId;
                }
            }
            this.reset();
            this.checkPreventiveSheetType();
            this.initCommands();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    /**
     * Initializes the commands available in the view header.
     */
    initCommands() {
        if (this.updateMode && (this.preventiveSheet.status === 'OPENED')) {
            this.commands.push(
                Command.handlerCommand('ui.preventivesheet.complete', 'images/svg/flag.svg',
                    () => this.showStatusUpdateConfirmationDialog('COMPLETED'), ['ADMIN', 'OPERATION_MANAGER', 'PREVENTIVE_SHEET_CLOSE']),
                Command.handlerCommand('ui.preventivesheet.addattachments', 'images/svg/attachment.svg',
                    () => this.showAttachmentViewDialog())
            );
        }
    }

    checkPreventiveSheetType() {
        if (this.preventiveSheet.equipmentName.match(/-SV$/)) {
            this.svType = true;
            this.initSvPreventiveSheet();
        }
    }

    initSvPreventiveSheet() {
        this.crossingIndex = 1;
    }

    /**
     * Initializes the maximum action date of a preventive sheet. If the state defines a planned week, it is used to
     * resolve the date, otherwise, the current date plus one year is used.
     *
     * @param {Object} preventiveSheet Preventive sheet.
     */
    initMaxActionDate(preventiveSheet) {
        if (this.$stateParams.plannedWeek) {
            preventiveSheet.maxActionDate = this.dateTimeService.toPreventiveSheetMaxActionDate(
                this.$stateParams.plannedWeek);
        } else {
            const maxActionDate = new Date();
            maxActionDate.setUTCFullYear(maxActionDate.getUTCFullYear() + 1);
            preventiveSheet.maxActionDate = this.dateTimeService.formatLocalDate(maxActionDate);
        }
    }

    /**
     * Loads equipments in a site.
     *
     * @param {Number} siteId Site ID.
     */
    loadEquipments(siteId) {
        if (siteId) {
            this.equipmentsDisabled = true;
            this.handleRestServiceCall(
                this.getEquipments(siteId),
                equipments => this.equipments = equipments
            ).catch(() => {
                this.notificationService.pushMessage('ui.preventivesheet.equipmentloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            }).finally(() => {
                this.equipmentsDisabled = false;
            });
        }
    }

    /**
     * Gets equipments in a site.
     *
     * @param {Number} siteId Site ID.
     * @return {Promise} Promise containing the equipments loaded.
     */
    getEquipments(siteId) {
        return this.equipmentService.findEntities({
            params: this.filterService.buildSortingParam({sort: 'name'}),
            data: this.filterService.buildFilters([
                Filter.equalFilter('site.id', siteId),
                Filter.notNullFilter('equipmentTemplate.preventiveSheetTemplate')
            ])
        });
    }

    /**
     * Shows a dialog containing the form to add attachments. When the dialog is closed, the view is refreshed.
     *
     * @return {Promise} A promise of the action.
     */
    showAttachmentViewDialog() {
        this.dialogService.showDialog(PreventiveSheetAttachmentViewController,
            'prevention/component/preventivesheetattachment-view.html', { preventiveSheetId: this.preventiveSheet.id}
        ).then(confirmed => {
            if (confirmed) {
                this.$onInit();
            }
        });
    }

    /**
     * Shows a confirmation dialog before changing the status. If the dialog is confirmed, the preventive sheet 'status
     * is updated.
     *
     * @param {status} New status.
     * @return {Promise} A promise of the action.
     */
    showStatusUpdateConfirmationDialog(status) {
        let messageKey;
        if (status === 'COMPLETED') {
            messageKey = 'ui.preventivesheet.completeconfirmation';
        }

        let promise;
        if (messageKey && this.updateMode) {
            promise = this.dialogService.showConfirmationDialogWithMessage(messageKey).then(confirmed => {
                if (confirmed) {
                    this.preventiveSheet.status = status;
                    this.save();
                }
            });
        } else {
            promise = this.$q.reject(false);
        }
        return promise;
    }

    /**
     * Maps a preventive sheet object to the input format expected by the remote API, before a save request can be sent.
     *
     * @param {object} preventiveSheet A preventive sheet.
     */
    mapToDto(preventiveSheet) {
        const convertedEntity = angular.copy(preventiveSheet);
        convertedEntity.maxActionDate = this.dateTimeService.toPreventiveSheetMaxActionDate(
            convertedEntity.plannedWeek);
        convertedEntity.checklist.forEach(check => {
            if (check.type === "DATE" && check.dateValue!= null ) {
               check.dateValue = this.dateTimeService.formatDate(check.dateValue)
            }
        });
        return convertedEntity;
    }

    addCrossing() {
        let duplicatedCrossing = false;
        let categoryToAdd = new RegExp( "P" + this.crossingIndex +"$", 'g' );

        this.preventiveSheet.category.forEach(category => {
            if (category.match(categoryToAdd)){
                duplicatedCrossing = true;
            }
        });

        if (this.crossingIndex > 0 && !duplicatedCrossing) {
            let originalCheckList = angular.copy(this.pristineEntity.checklist);
            originalCheckList.forEach(check => {
                if (check.category.match(/Traversée P0$/)) {
                    let newCheck = angular.copy(check);
                    newCheck.id = null;
                    newCheck.category = check.category.slice(0, -1) + this.crossingIndex;
                    newCheck.key = check.key.slice(0, -1) + this.crossingIndex;
                    let name = check.name.replace("P0", "");
                    newCheck.name = "P" + this.crossingIndex + " " + name;
                    this.preventiveSheet.checklist.push(newCheck);
                }
            });
            this.preventiveSheet = new PreventiveSheet(this.preventiveSheet);
        }
    }

    removeCrossing() {
        if (this.crossingIndex > 0) {
            let checkList = angular.copy(this.preventiveSheet.checklist);
            let categoryToDelete = new RegExp("Traversée P" + this.crossingIndex.toString() + "$");
            checkList = checkList.filter(check => {
                return !(check.category.match(categoryToDelete));
            });
            this.preventiveSheet.checklist = checkList;
            this.preventiveSheet = new PreventiveSheet(this.preventiveSheet);
        }
    }

    save() {
        this.formDisabled = true;
        const dto = this.mapToDto(this.preventiveSheet);
        let serviceActionPromise;
        if (this.updateMode) {
            serviceActionPromise = this.preventiveSheetService.updateEntity(dto);
        } else {
            serviceActionPromise = this.preventiveSheetService.createEntity(dto);
        }
        this.handleRestServiceCall(serviceActionPromise, () => {
            const message = 'ui.preventivesheet.' + (this.updateMode ? 'updated' : 'created');
            this.notificationService.pushMessage(message, this.configuration.NOTIFIER_LEVEL_INFO);
            if (this.updateMode) {
                this.$state.reload();
            } else {
                this.$state.go('gmao.preventivesheet.list');
            }
        }).then(data => {

            if (data && data.errors && data.errors["PREVENTIVE_SHEET_CHECK_CONSTRAINT_VIOLATION"]) {
                this.checkConstraintViolation = data.errors["PREVENTIVE_SHEET_CHECK_CONSTRAINT_VIOLATION"].message
            }
        }).finally(() => {
            this.formDisabled = false;
        });
    }

    /**
     * Restores the initial state of the view, and the map.
     */
    reset() {
        this.preventiveSheet = angular.copy(this.pristineEntity);
    }

    isComplete() {
        return this.preventiveSheet && this.preventiveSheet.status === 'COMPLETED' &&
            !this.authenticationService.userHasRole(["ADMIN", "OPERATION_MANAGER"])
    }
}

export const preventiveSheetViewComponent = {
    template: require('./preventivesheet-view.html'),
    controller: PreventiveSheetViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/prevention/component/PreventiveSheetView.js